import styled from "@emotion/styled";
import { Box } from "@otrium/core";
import { hideScrollbar, scrollbar } from "src/scrollbar";

export const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
  overscroll-behavior: contain;
  touch-action: pan-y;
`;

export const CloseButtonWrapper = styled.div`
  position: absolute;
  right: 0;
`;

export const Overlay = styled.div<{ isOpen: boolean; zIndex?: number }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: ${({ theme, zIndex }) => zIndex ?? theme.zIndex.drawer};
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  ${({ isOpen }) => isOpen && `touch-action: none;`}
`;

export const ProductImage = styled.img`
  width: 100px;
  height: 150px;
  object-fit: contain;
`;

export const SchemaSelectorRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  padding: 16px;
  align-items: center;
`;

export const SchemaSelectorButton = styled.button`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  background: ${({ theme }) => theme.colors.tone.white};
  color: ${({ theme }) => theme.colors.tone.anthracite};
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  line-height: 24px;
  justify-content: space-between;
  -webkit-tap-highlight-color: transparent;
  outline: none;

  &::after {
    content: "";
    display: inline-block;
    -webkit-mask-image: url(/static/images/chevron-down.svg);
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    -webkit-mask-size: contain;
    mask-image: url(/static/images/chevron-down.svg);
    mask-repeat: no-repeat;
    mask-position: center;
    mask-size: contain;
    background-color: ${({ theme }) => theme.colors.tone.anthracite};
    width: 16px;
    height: 16px;
  }
`;

export const SizeListContainer = styled(Box)<{
  scrollLocked?: boolean;
  hasSizeFitLabel?: boolean;
}>`
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: ${({ scrollLocked }) => (scrollLocked ? "hidden" : "auto")};
  overscroll-behavior: contain;
  margin-left: 0;
  margin-right: 4px;
  margin-bottom: ${({ hasSizeFitLabel }) =>
    hasSizeFitLabel ? "130px" : "110px"};
  padding-left: 16px;
  padding-right: 16px;
  ${scrollbar}
`;

export const DrawerFooter = styled.div`
  position: fixed;
  bottom: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
  background: ${({ theme }) => theme.colors.tone.white};
  box-shadow: 0 -4px 8px 0 ${({ theme }) => theme.effects.overlay.black_10};
  padding: 16px;
`;

export const SizeRow = styled.button<{
  isSelected?: boolean;
  isOutOfStock?: boolean;
}>`
  display: grid;
  grid-template-columns: 1fr 2fr;
  width: calc(100% + 32px);
  margin-left: -16px;
  padding: 10px 16px;
  align-items: center;
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.tone.sand : "transparent"};
  border: none;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  outline: none;

  &:active {
    background: ${({ theme }) => theme.colors.tone.sand};
  }
`;

export const BrandSizeCell = styled.span<{
  isSelected?: boolean;
  isOutOfStock?: boolean;
}>`
  flex: 0 0 50%;
  font-size: 16px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  line-height: 24px;
  color: ${({ isOutOfStock }) =>
    isOutOfStock ? "rgba(0, 0, 0, 0.4)" : "#000"};
  text-align: left;
`;

export const MappedSizeCell = styled.div`
  flex: 0 0 50%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 16px;
`;

export const MappedSizeValue = styled.span<{
  isSelected?: boolean;
  isOutOfStock?: boolean;
}>`
  font-size: 16px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  line-height: 24px;
  color: ${({ isSelected, isOutOfStock }) =>
    isOutOfStock ? "rgba(0, 0, 0, 0.4)" : isSelected ? "#000" : "#6b6b6b"};
  text-align: left;
`;

export const StockIndicator = styled.span<{ isSelected?: boolean }>`
  font-size: 14px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  line-height: 20px;
  color: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.tone.black : theme.colors.tone.anthracite};
  white-space: nowrap;
  text-align: right;
`;

export const NotifyMeIndicator = styled.span`
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #000;
  white-space: nowrap;
  text-align: right;
`;

export const SchemaDrawerContainer = styled.div<{ isOpen: boolean }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: ${({ theme }) => theme.colors.tone.white};
  box-shadow: 0 -4px 8px 0 ${({ theme }) => theme.effects.overlay.black_10};
  border-radius: 16px 16px 0 0;
  z-index: ${({ theme }) => theme.zIndex.drawer + 2};
  transform: ${({ isOpen }) => (isOpen ? "translateY(0)" : "translateY(100%)")};
  visibility: ${({ isOpen }) => (isOpen ? "visible" : "hidden")};
  transition: transform 0.3s ease;
  max-height: 53vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const SchemaList = styled(Box)`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  padding: 32px 0;
  margin-bottom: 72px;
  ${hideScrollbar}
`;

export const SchemaOption = styled.button<{ isSelected?: boolean }>`
  display: flex;
  width: 100%;
  padding: 10px 16px;
  align-items: center;
  justify-content: center;
  background: ${({ theme, isSelected }) =>
    isSelected ? theme.colors.tone.sand : "transparent"};
  border: none;
  cursor: pointer;
  font-size: 16px;
  font-weight: ${({ isSelected }) => (isSelected ? 600 : 400)};
  line-height: 24px;
  color: ${({ theme }) => theme.colors.tone.black};
  -webkit-tap-highlight-color: transparent;
  outline: none;

  &:active {
    background: ${({ theme }) => theme.colors.tone.sand};
  }
`;
